/**
 * Acquisition Kanban View
 * 
 * Uses the global Kanban component with acquisitions-specific configuration.
 * All column definitions come from acquisitionPhaseConfig.ts
 */

import { useState, useEffect } from 'react';
import { Acquisition, AcquisitionPhase } from 'shared/types/acquisition';
import { AcquisitionCard } from './AcquisitionCard';
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Kanban } from 'shared/components/Kanban';
import { acquisitionsKanbanConfig } from 'shared/components/Kanban/configs/acquisitionsKanbanConfig';

interface AcquisitionKanbanViewProps {
  acquisitions: Acquisition[];
  onUpdate: () => void;
  onAcquisitionClick: (acquisition: Acquisition) => void;
  onAddClick?: (phase: AcquisitionPhase) => void;
  onCompleteAcquisition?: (acquisition: Acquisition) => void;
}

export const AcquisitionKanbanView = ({
  acquisitions,
  onUpdate,
  onAcquisitionClick,
  onAddClick,
  onCompleteAcquisition
}: AcquisitionKanbanViewProps) => {
  const { session } = useSession();
  
  // Local state for optimistic updates - filter out items without IDs
  const [localAcquisitions, setLocalAcquisitions] = useState(
    acquisitions.filter(a => a.id !== undefined) as Array<Acquisition & { id: number }>
  );
  
  // Update local state when prop changes
  useEffect(() => {
    setLocalAcquisitions(
      acquisitions.filter(a => a.id !== undefined) as Array<Acquisition & { id: number }>
    );
  }, [acquisitions]);

  /**
   * Handle drop event from the Kanban component
   * Performs optimistic update and syncs with backend
   */
  const handleDrop = async (
    itemId: string | number,
    newPhase: AcquisitionPhase,
    oldPhase: AcquisitionPhase
  ) => {
    const acquisitionId = itemId as number;

    // Find the acquisition being dragged
    const acquisition = localAcquisitions.find(a => a.id === acquisitionId);
    if (!acquisition) {
      return;
    }

    // If phase hasn't changed, no need to update
    if (acquisition.acquisitionPhase === newPhase) {
      return;
    }

    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalAcquisitions((prev) => 
      prev.map(a => 
        a.id === acquisitionId 
          ? { ...a, acquisitionPhase: newPhase }
          : a
      )
    );

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions/update-phase`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ acquisitionIds: [acquisitionId], phase: newPhase }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to update acquisition phase");
      }

      toast.success(`Acquisitie verplaatst naar "${newPhase}"`);
    } catch (error: any) {
      console.error("Error updating acquisition phase:", error);
      toast.error(`Fout bij verplaatsen acquisitie: ${error.message || 'Onbekende fout'}`);
      
      // ROLLBACK: Revert local state on error
      setLocalAcquisitions((prev) => 
        prev.map(a => 
          a.id === acquisitionId 
            ? { ...a, acquisitionPhase: oldPhase }
            : a
        )
      );
      
      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  /**
   * Handle add click from the Kanban component
   */
  const handleAddClick = (phase: AcquisitionPhase) => {
    if (onAddClick) {
      onAddClick(phase);
    }
  };

  /**
   * Render an acquisition card
   */
  const renderAcquisitionCard = (acquisition: Acquisition) => (
    <AcquisitionCard
      acquisition={acquisition}
      onClick={() => onAcquisitionClick(acquisition)}
      onUpdate={onUpdate}
      onCompleteAcquisition={onCompleteAcquisition}
    />
  );

  return (
    // @ts-ignore - Type mismatch between Acquisition and KanbanItem due to optional id
    <Kanban
      config={acquisitionsKanbanConfig}
      items={localAcquisitions}
      renderCard={renderAcquisitionCard}
      onDrop={handleDrop}
      onAddClick={handleAddClick}
      onCardClick={onAcquisitionClick}
    />
  );
};
